import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import {
  ancestorDirPaths,
  filterHiddenTreeNodes,
  normalizeWorkspacePath,
  resolveWorkspaceRelPath,
  sameWorkspacePath,
} from "../../utils/youlaberWorkspacePath";

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp"]);

const getFileIcon = (path, isDir = false) => {
  if (isDir) return "📁";
  const ext = path.split(".").pop()?.toLowerCase();
  const icons = {
    js: "📜", ts: "📜", jsx: "📜", tsx: "📜",
    py: "🐍", go: "🔵", java: "☕", ino: "🔌",
    md: "📝", txt: "📄", json: "📋", yaml: "📋", yml: "📋",
    png: "🖼️", jpg: "🖼️", jpeg: "🖼️", gif: "🖼️", svg: "🖼️",
    html: "🌐", css: "🎨", csv: "📊", sh: "⚡", bat: "⚡",
  };
  return icons[ext] || "📄";
};

const detectLanguage = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  const map = {
    md: "markdown", markdown: "markdown",
    js: "javascript", jsx: "javascript",
    ts: "typescript", tsx: "typescript",
    py: "python", go: "go", java: "java",
    json: "json", yaml: "yaml", yml: "yaml",
    html: "html", css: "css", csv: "plaintext",
    ino: "cpp", cpp: "cpp", c: "c", h: "c",
    sh: "shell", bat: "bat", txt: "plaintext",
  };
  return map[ext] || "plaintext";
};

const CCFileTree = ({
  ccFileTree,
  deviceId,
  apiUrl,
  openPathRequest,
  treeVisible = true,
  editorVisible = true,
  onTreeVisibleChange,
  onEditorVisibleChange,
  onEditorOpen,
}) => {
  const baseUrl = (apiUrl || "").replace(/\/$/, "");
  const [treeData, setTreeData] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState("");
  const [tabs, setTabs] = useState([]);
  const [activePath, setActivePath] = useState("");
  const [savingPath, setSavingPath] = useState("");
  const [selectedDir, setSelectedDir] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [treeWidth, setTreeWidth] = useState(220);
  const [expandedDirs, setExpandedDirs] = useState(() => new Set());
  const [moreOpen, setMoreOpen] = useState(false);
  const autoSaveTimers = useRef(new Map());
  const handledChangedAt = useRef({});
  const dragState = useRef(null);
  const lastOpenRequestTs = useRef(0);
  const seededExpand = useRef(false);
  const openingPaths = useRef(new Set());
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const prevDeviceId = useRef(deviceId);

  const clearAutoSave = useCallback((path) => {
    const key = normalizeWorkspacePath(path);
    const timer = autoSaveTimers.current.get(key);
    if (timer) {
      window.clearTimeout(timer);
      autoSaveTimers.current.delete(key);
    }
  }, []);

  const clearAllAutoSaves = useCallback(() => {
    autoSaveTimers.current.forEach((timer) => window.clearTimeout(timer));
    autoSaveTimers.current.clear();
  }, []);

  const activeTab = useMemo(
    () => tabs.find((tab) => sameWorkspacePath(tab.path, activePath)) || null,
    [tabs, activePath],
  );

  const revealInTree = useCallback((filePath) => {
    const rel = normalizeWorkspacePath(filePath);
    if (!rel) return;
    setExpandedDirs((prev) => {
      const next = new Set(prev);
      ancestorDirPaths(rel).forEach((dir) => next.add(dir));
      return next;
    });
    setSelectedPath(rel);
    setActivePath(rel);
  }, []);

  const endpoint = useCallback((name, query = "") => {
    if (!baseUrl || !deviceId) return "";
    return `${baseUrl}/api/claw/workspace/${deviceId}/${name}${query}`;
  }, [baseUrl, deviceId]);

  const refreshTree = useCallback(async () => {
    if (!deviceId || !baseUrl) return;
    setLoadingTree(true);
    setTreeError("");
    try {
      const res = await fetch(endpoint("tree"));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTreeData(filterHiddenTreeNodes(Array.isArray(data.tree) ? data.tree : []));
    } catch (e) {
      setTreeError(e.message);
    } finally {
      setLoadingTree(false);
    }
  }, [baseUrl, deviceId, endpoint]);

  useEffect(() => {
    refreshTree();
  }, [refreshTree]);

  useEffect(() => {
    if (prevDeviceId.current === deviceId) return;
    prevDeviceId.current = deviceId;
    clearAllAutoSaves();
    openingPaths.current.clear();
    handledChangedAt.current = {};
    lastOpenRequestTs.current = 0;
    seededExpand.current = false;
    setTabs([]);
    setActivePath("");
    setSelectedDir("");
    setSelectedPath("");
    setTreeData([]);
    setTreeError("");
    setExpandedDirs(new Set());
    onEditorVisibleChange && onEditorVisibleChange(false);
  }, [deviceId, clearAllAutoSaves, onEditorVisibleChange]);

  useEffect(() => () => clearAllAutoSaves(), [clearAllAutoSaves]);

  useEffect(() => {
    if (seededExpand.current || !treeData.length) return;
    seededExpand.current = true;
    setExpandedDirs(new Set(
      treeData.filter((node) => node.is_dir).map((node) => normalizeWorkspacePath(node.path)),
    ));
  }, [treeData]);

  const readFile = useCallback(async (path) => {
    const res = await fetch(endpoint("file", `?path=${encodeURIComponent(path)}`));
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data.content || "";
  }, [endpoint]);

  const saveFile = useCallback(async (path, content) => {
    setSavingPath(path);
    try {
      const res = await fetch(endpoint("file"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, content }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTabs((prev) => prev.map((tab) => (
        tab.path === path ? { ...tab, originalContent: content, dirty: false } : tab
      )));
      refreshTree();
    } finally {
      setSavingPath("");
    }
  }, [endpoint, refreshTree]);

  const dropTabsByPrefix = useCallback((oldPath) => {
    const prefix = normalizeWorkspacePath(oldPath);
    clearAutoSave(oldPath);
    setTabs((prev) => {
      const next = prev.filter((tab) => {
        const p = normalizeWorkspacePath(tab.path);
        const keep = p !== prefix && !p.startsWith(`${prefix}/`);
        if (!keep) clearAutoSave(tab.path);
        return keep;
      });
      if (next.length === 0) {
        setActivePath("");
        onEditorVisibleChange && onEditorVisibleChange(false);
      } else if (!next.some((tab) => sameWorkspacePath(tab.path, activePath))) {
        setActivePath(next[next.length - 1].path);
        setSelectedPath(next[next.length - 1].path);
      }
      return next;
    });
  }, [activePath, clearAutoSave, onEditorVisibleChange]);

  const openFile = useCallback(async (node) => {
    if (!node || node.is_dir) {
      return { ok: false, error: "无法打开：不是文件" };
    }
    let resolved = node.fromCard
      ? resolveWorkspaceRelPath(node.path, treeData)
      : { path: node.path, name: node.name };
    if (resolved.error && !resolved.path) {
      setTreeError(resolved.error);
      return { ok: false, error: resolved.error };
    }
    const path = resolved.path;
    const name = resolved.name || path.split("/").pop() || path;
    const key = normalizeWorkspacePath(path);
    if (openingPaths.current.has(key)) return { ok: true };
    onEditorOpen && onEditorOpen();
    revealInTree(path);
    if (tabsRef.current.find((tab) => sameWorkspacePath(tab.path, path))) return { ok: true };
    openingPaths.current.add(key);
    try {
      const ext = path.split(".").pop()?.toLowerCase();
      if (IMAGE_EXTS.has(ext)) {
        const url = `${baseUrl}/api/claw/files/${deviceId}/${path}`;
        setTabs((prev) => {
          if (prev.some((tab) => sameWorkspacePath(tab.path, path))) return prev;
          return [...prev, {
            path,
            name,
            content: "",
            originalContent: "",
            language: "image",
            imageUrl: url,
            dirty: false,
            pendingChange: null,
          }];
        });
        return { ok: true };
      }
      const content = await readFile(path);
      setTabs((prev) => {
        if (prev.some((tab) => sameWorkspacePath(tab.path, path))) return prev;
        return [...prev, {
          path,
          name,
          content,
          originalContent: content,
          language: detectLanguage(name),
          dirty: false,
          pendingChange: null,
        }];
      });
      return { ok: true };
    } catch (e) {
      const msg = `读取失败：${e.message}`;
      setTreeError(msg);
      if (node.fromCard) refreshTree();
      return { ok: false, error: msg };
    } finally {
      openingPaths.current.delete(key);
    }
  }, [baseUrl, deviceId, onEditorOpen, readFile, refreshTree, revealInTree, treeData]);

  useEffect(() => {
    const req = openPathRequest;
    if (!req?.ts || req.ts === lastOpenRequestTs.current) return;
    lastOpenRequestTs.current = req.ts;
    const finish = (ok, error) => {
      if (typeof req.resolve === "function") req.resolve({ ok, error: error || "" });
    };
    openFile({ path: req.path, name: req.path, is_dir: false, fromCard: true })
      .then((result) => {
        if (!result || result.ok === false) finish(false, result?.error);
        else finish(true);
      })
      .catch((e) => finish(false, e.message));
  }, [openPathRequest, openFile]);

  const updateTabContent = (path, content) => {
    setTabs((prev) => prev.map((tab) => (
      tab.path === path ? { ...tab, content, dirty: content !== tab.originalContent } : tab
    )));
    clearAutoSave(path);
    const timer = window.setTimeout(() => {
      autoSaveTimers.current.delete(normalizeWorkspacePath(path));
      saveFile(path, content).catch((e) => setTreeError(`保存失败：${e.message}`));
    }, 1500);
    autoSaveTimers.current.set(normalizeWorkspacePath(path), timer);
  };

  const closeTab = (path) => {
    const tab = tabs.find((item) => sameWorkspacePath(item.path, path));
    if (tab?.dirty && !window.confirm(`"${tab.name}" 有未保存内容，确定关闭？`)) return;
    clearAutoSave(path);
    const nextTabs = tabs.filter((item) => !sameWorkspacePath(item.path, path));
    setTabs(nextTabs);
    if (nextTabs.length === 0) {
      setActivePath("");
      onEditorVisibleChange && onEditorVisibleChange(false);
      return;
    }
    if (sameWorkspacePath(activePath, path)) {
      const idx = tabs.findIndex((item) => sameWorkspacePath(item.path, path));
      const next = nextTabs[idx - 1] || nextTabs[idx] || nextTabs[0];
      setActivePath(next.path);
      setSelectedPath(next.path);
    }
  };

  const createEntry = async (isDir) => {
    const name = window.prompt(isDir ? "请输入目录名" : "请输入文件名");
    if (!name) return;
    const path = selectedDir ? `${selectedDir}/${name}` : name;
    try {
      const res = await fetch(endpoint("file"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, is_dir: isDir }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      refreshTree();
    } catch (e) {
      setTreeError(`创建失败：${e.message}`);
    }
  };

  const downloadWorkspace = async () => {
    const downloadPath = selectedPath || activePath || selectedDir;
    try {
      const res = await fetch(endpoint("file"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ download_paths: [downloadPath || ""] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${downloadPath ? downloadPath.replace(/[\\/]/g, "-") : "youlaber-workspace"}-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setTreeError(`下载失败：${e.message}`);
    }
  };

  const startTreeDrag = (e) => {
    dragState.current = { startX: e.clientX, startWidth: treeWidth };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const saveActiveTab = useCallback(() => {
    if (!activeTab || activeTab.language === "image" || activeTab.pendingChange) return;
    saveFile(activeTab.path, activeTab.content).catch((e) => setTreeError(`保存失败：${e.message}`));
  }, [activeTab, saveFile]);

  const renameEntry = async (node) => {
    const newName = window.prompt("请输入新名称", node.name);
    if (!newName || newName === node.name) return;
    const parts = node.path.split("/");
    parts[parts.length - 1] = newName;
    try {
      const res = await fetch(endpoint("rename"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ old_path: node.path, new_path: parts.join("/") }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      dropTabsByPrefix(node.path);
      refreshTree();
    } catch (e) {
      setTreeError(`重命名失败：${e.message}`);
    }
  };

  const deleteEntry = async (node) => {
    if (!window.confirm(`确定删除 "${node.path}"？`)) return;
    try {
      const res = await fetch(endpoint("file", `?path=${encodeURIComponent(node.path)}`), { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      dropTabsByPrefix(node.path);
      refreshTree();
    } catch (e) {
      setTreeError(`删除失败：${e.message}`);
    }
  };

  const acceptDiff = (path) => {
    setTabs((prev) => prev.map((tab) => {
      if (tab.path !== path || !tab.pendingChange) return tab;
      return {
        ...tab,
        content: tab.pendingChange.newContent,
        originalContent: tab.pendingChange.newContent,
        dirty: false,
        pendingChange: null,
      };
    }));
  };

  const rejectDiff = async (path) => {
    const tab = tabs.find((item) => item.path === path);
    if (!tab?.pendingChange) return;
    try {
      await saveFile(path, tab.pendingChange.oldContent);
      setTabs((prev) => prev.map((item) => (
        item.path === path
          ? { ...item, content: tab.pendingChange.oldContent, originalContent: tab.pendingChange.oldContent, dirty: false, pendingChange: null }
          : item
      )));
    } catch (e) {
      setTreeError(`还原失败：${e.message}`);
    }
  };

  useEffect(() => {
    const changed = Object.entries(ccFileTree || {})
      .filter(([, meta]) => meta?.changed && meta.modified)
      .sort((a, b) => a[1].modified - b[1].modified);
    changed.forEach(([path, meta]) => {
      if (handledChangedAt.current[path] === meta.modified) return;
      handledChangedAt.current[path] = meta.modified;
      refreshTree();
      const tab = tabs.find((item) => item.path === path);
      if (!tab || tab.language === "image") return;
      readFile(path).then((newContent) => {
        setTabs((prev) => prev.map((item) => {
          if (item.path !== path || item.content === newContent) return item;
          return {
            ...item,
            pendingChange: { oldContent: item.content, newContent },
          };
        }));
        setActivePath(path);
        onEditorOpen && onEditorOpen();
      }).catch(() => {});
    });
  }, [ccFileTree, onEditorOpen, readFile, refreshTree, tabs]);

  useEffect(() => {
    const onKeydown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveActiveTab();
      }
    };
    window.addEventListener("keydown", onKeydown);
    return () => window.removeEventListener("keydown", onKeydown);
  }, [saveActiveTab]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState.current) return;
      const next = dragState.current.startWidth + (e.clientX - dragState.current.startX);
      setTreeWidth(Math.max(170, Math.min(360, next)));
    };
    const onUp = () => {
      dragState.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const showTree = treeVisible !== false;
  const showEditor = editorVisible === true;

  return (
    <div style={{ display: "flex", height: "100%", width: "100%", background: "#0d1117", color: "#e6edf3" }}>
      {!showTree && (
        <div style={{ width: 44, borderRight: "1px solid #21262d", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, paddingTop: 8, flexShrink: 0 }}>
          <button title="打开文件目录" onClick={() => onTreeVisibleChange && onTreeVisibleChange(true)} style={railBtn}>文件</button>
          {activeTab && !showEditor && (
            <button title="打开文本编辑栏" onClick={() => onEditorVisibleChange && onEditorVisibleChange(true)} style={railBtn}>编辑</button>
          )}
        </div>
      )}

      {showTree && (
      <div style={{ width: treeWidth, display: "flex", flexDirection: "column", minWidth: 0, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 9px", borderBottom: "1px solid #21262d", fontSize: 12, position: "relative" }}>
          <b style={{ flex: 1, color: "#9aa4b2" }}>工作区</b>
          <button title="刷新" onClick={refreshTree} style={iconBtn}>刷新</button>
          <button title="更多操作" onClick={() => setMoreOpen((v) => !v)} style={iconBtn}>更多</button>
          <button title="关闭文件目录栏" onClick={() => onTreeVisibleChange && onTreeVisibleChange(false)} style={iconBtn}>收起</button>
          {moreOpen && (
            <div style={{
              position: "absolute",
              top: 34,
              right: 8,
              zIndex: 8,
              background: "#161b22",
              border: "1px solid #30363d",
              borderRadius: 6,
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 108,
            }}>
              <button onClick={() => { setMoreOpen(false); createEntry(false); }} style={menuBtn}>新建文件</button>
              <button onClick={() => { setMoreOpen(false); createEntry(true); }} style={menuBtn}>新建目录</button>
              <button onClick={() => { setMoreOpen(false); downloadWorkspace(); }} style={menuBtn}>下载选中</button>
              <button onClick={() => { setMoreOpen(false); setExpandedDirs(new Set()); }} style={menuBtn}>全部折叠</button>
              <button onClick={() => { setMoreOpen(false); activePath && revealInTree(activePath); }} style={menuBtn}>定位当前</button>
            </div>
          )}
        </div>
        <div style={{ padding: "4px 9px", color: "#6e7681", fontSize: 11, borderBottom: "1px solid #161b22" }}>
          当前：{selectedPath || selectedDir || "/"}
        </div>
        {treeError && <div style={{ color: "#f85149", fontSize: 11, padding: 8 }}>{treeError}</div>}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 4px" }}>
          {loadingTree && <div style={{ color: "#6e7681", padding: 8, fontSize: 12 }}>加载中...</div>}
          {!loadingTree && treeData.length === 0 && <div style={{ color: "#6e7681", padding: 12, fontSize: 12 }}>工作区暂无文件</div>}
          {treeData.map((node) => (
            <TreeNode
              key={node.path}
              node={node}
              activePath={activePath}
              selectedPath={selectedPath}
              expandedDirs={expandedDirs}
              onToggleDir={(path) => {
                const key = normalizeWorkspacePath(path);
                setExpandedDirs((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
              onOpen={openFile}
              onSelectDir={(path) => { setSelectedDir(path); setSelectedPath(path); }}
              onSelectPath={setSelectedPath}
              onRename={renameEntry}
              onDelete={deleteEntry}
            />
          ))}
        </div>
      </div>
      )}

      {showTree && showEditor && (
        <div
        onMouseDown={startTreeDrag}
        title="拖动调整文件树宽度"
        style={{ width: 5, cursor: "col-resize", background: "#161b22", borderLeft: "1px solid #21262d", borderRight: "1px solid #21262d", flexShrink: 0 }}
        />
      )}

      {!showEditor && showTree && activeTab && (
        <div style={{ width: 52, borderLeft: "1px solid #21262d", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 8, flexShrink: 0 }}>
          <button title={`打开文本编辑栏：${activeTab.path}`} onClick={() => onEditorVisibleChange && onEditorVisibleChange(true)} style={railBtn}>编辑</button>
        </div>
      )}

      {showEditor && (
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ height: 34, display: "flex", borderBottom: "1px solid #21262d", overflowX: "auto", flexShrink: 0 }}>
          <button
            title="保存当前文件 Ctrl+S"
            disabled={!activeTab || activeTab.language === "image" || activeTab.pendingChange}
            onClick={saveActiveTab}
            style={{
              border: "none",
              borderRight: "1px solid #21262d",
              background: "#161b22",
              color: activeTab?.dirty ? "#d29922" : "#6e7681",
              padding: "0 10px",
              cursor: activeTab && activeTab.language !== "image" && !activeTab.pendingChange ? "pointer" : "not-allowed",
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            保存
          </button>
          <button
            title="关闭文本编辑栏"
            onClick={() => onEditorVisibleChange && onEditorVisibleChange(false)}
            style={{
              border: "none",
              borderRight: "1px solid #21262d",
              background: "#161b22",
              color: "#9aa4b2",
              padding: "0 10px",
              cursor: "pointer",
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            隐藏编辑
          </button>
          {tabs.map((tab) => (
            <button
              key={tab.path}
              onClick={() => {
                setActivePath(tab.path);
                setSelectedPath(tab.path);
              }}
              style={{
                border: "none",
                borderRight: "1px solid #21262d",
                background: activePath === tab.path ? "#161b22" : "#0d1117",
                color: activePath === tab.path ? "#58a6ff" : "#9aa4b2",
                padding: "0 10px",
                cursor: "pointer",
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span>{tab.name}</span>
              {tab.pendingChange ? <span style={{ color: "#3fb950" }}>◆</span> : tab.dirty ? <span style={{ color: "#d29922" }}>●</span> : null}
              <span onClick={(e) => { e.stopPropagation(); closeTab(tab.path); }} style={{ color: "#6e7681" }}>×</span>
            </button>
          ))}
        </div>

        {activeTab?.pendingChange && (
          <div style={{ padding: "6px 10px", background: "#2a2111", borderBottom: "1px solid #6e4f17", display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <span style={{ color: "#d29922", flex: 1 }}>AI 修改了当前文件，请审阅后接受或拒绝。</span>
            <button onClick={() => acceptDiff(activeTab.path)} style={okBtn}>接受</button>
            <button onClick={() => rejectDiff(activeTab.path)} style={dangerBtn}>拒绝</button>
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0 }}>
          {!activeTab && (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#6e7681", fontSize: 13 }}>
              点击左侧文件开始编辑
            </div>
          )}
          {activeTab?.language === "image" && (
            <div style={{ height: "100%", overflow: "auto", padding: 12 }}>
              <img src={activeTab.imageUrl} alt={activeTab.name} style={{ maxWidth: "100%", borderRadius: 6 }} />
            </div>
          )}
          {activeTab && activeTab.language !== "image" && !activeTab.pendingChange && (
            <Editor
              height="100%"
              language={activeTab.language}
              value={activeTab.content}
              theme="vs-dark"
              onChange={(value) => updateTabContent(activeTab.path, value || "")}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                wordWrap: "on",
                automaticLayout: true,
                renderValidationDecorations: "off",
                unicodeHighlight: {
                  ambiguousCharacters: false,
                  invisibleCharacters: false,
                  nonBasicASCII: false,
                },
              }}
            />
          )}
          {activeTab?.pendingChange && (
            <DiffPreview
              oldContent={activeTab.pendingChange.oldContent}
              newContent={activeTab.pendingChange.newContent}
            />
          )}
        </div>
        <div style={{ height: 24, borderTop: "1px solid #21262d", padding: "3px 9px", color: "#6e7681", fontSize: 11 }}>
          {savingPath ? `保存中：${savingPath}` : activeTab ? activeTab.path : "就绪"}
        </div>
      </div>
      )}
    </div>
  );
};

const DiffPreview = ({ oldContent = "", newContent = "" }) => (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "#0d1117" }}>
    <div style={{ height: 28, padding: "5px 10px", color: "#9aa4b2", background: "#161b22", borderBottom: "1px solid #30363d", fontSize: 12 }}>
      修改对比：红色为删除，绿色为新增
    </div>
    <div style={{ flex: 1, overflow: "auto", padding: "8px 0" }}>
      {buildLineDiff(oldContent, newContent).map((line, index) => (
        <div
          key={`${line.type}-${index}`}
          style={{
            display: "grid",
            gridTemplateColumns: "44px 24px 1fr",
            minHeight: 22,
            fontFamily: "'Cascadia Code', Consolas, monospace",
            fontSize: 12,
            lineHeight: "22px",
            background: line.type === "add"
              ? "rgba(46, 160, 67, 0.18)"
              : line.type === "del"
                ? "rgba(248, 81, 73, 0.18)"
                : "transparent",
            color: line.type === "add"
              ? "#b7f7c8"
              : line.type === "del"
                ? "#ffd0d0"
                : "#d0d7de",
            borderLeft: line.type === "add"
              ? "3px solid #3fb950"
              : line.type === "del"
                ? "3px solid #f85149"
                : "3px solid transparent",
          }}
        >
          <span style={{ color: "#6e7681", textAlign: "right", paddingRight: 8, userSelect: "none" }}>{line.no || ""}</span>
          <span style={{ color: line.type === "add" ? "#3fb950" : line.type === "del" ? "#f85149" : "#6e7681", userSelect: "none" }}>
            {line.type === "add" ? "+" : line.type === "del" ? "-" : " "}
          </span>
          <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", paddingRight: 10 }}>{line.text || " "}</span>
        </div>
      ))}
    </div>
  </div>
);

const buildLineDiff = (oldContent, newContent) => {
  const oldLines = String(oldContent || "").split(/\r?\n/);
  const newLines = String(newContent || "").split(/\r?\n/);
  const rows = [];
  let i = 0;
  let j = 0;
  let displayNo = 1;

  while (i < oldLines.length || j < newLines.length) {
    if (i < oldLines.length && j < newLines.length && oldLines[i] === newLines[j]) {
      rows.push({ type: "same", text: oldLines[i], no: displayNo++ });
      i += 1;
      j += 1;
      continue;
    }

    const nextOldMatch = findNextMatch(oldLines, newLines[j], i + 1);
    const nextNewMatch = findNextMatch(newLines, oldLines[i], j + 1);

    if (j < newLines.length && (nextOldMatch === -1 || (nextNewMatch !== -1 && nextNewMatch - j <= nextOldMatch - i))) {
      rows.push({ type: "add", text: newLines[j], no: displayNo++ });
      j += 1;
      continue;
    }

    if (i < oldLines.length) {
      rows.push({ type: "del", text: oldLines[i], no: "" });
      i += 1;
      continue;
    }

    rows.push({ type: "add", text: newLines[j], no: displayNo++ });
    j += 1;
  }

  return rows;
};

const findNextMatch = (lines, target, start) => {
  if (target == null) return -1;
  const limit = Math.min(lines.length, start + 20);
  for (let i = start; i < limit; i += 1) {
    if (lines[i] === target) return i;
  }
  return -1;
};

const TreeNode = ({
  node, activePath, selectedPath, expandedDirs, onToggleDir,
  onOpen, onSelectDir, onSelectPath, onRename, onDelete, level = 0,
}) => {
  const rowRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const isActive = sameWorkspacePath(activePath, node.path);
  const isSelected = sameWorkspacePath(selectedPath, node.path);
  const open = node.is_dir && expandedDirs.has(normalizeWorkspacePath(node.path));

  useEffect(() => {
    if (isActive && rowRef.current) {
      rowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [isActive]);

  return (
    <div>
      <div
        ref={rowRef}
        draggable={!node.is_dir}
        onDragStart={(e) => {
          e.dataTransfer.setData("application/x-file-path", node.path);
          e.dataTransfer.setData("text/plain", node.path);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => {
          onSelectPath(node.path);
          if (node.is_dir) {
            onSelectDir(node.path);
            onToggleDir(node.path);
          } else {
            onOpen(node);
          }
        }}
        style={{
          padding: "4px 6px",
          paddingLeft: 6 + level * 12,
          display: "flex",
          alignItems: "center",
          gap: 5,
          borderRadius: 5,
          cursor: "pointer",
          fontSize: 12,
          background: isActive ? "#1f2937" : isSelected ? "#161b22" : "transparent",
          color: isActive ? "#58a6ff" : "#d0d7de",
          fontWeight: isActive ? 600 : 400,
        }}
      >
        <span>{getFileIcon(node.name, node.is_dir)}</span>
        <span title={node.path} style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
        {hovered && (
          <>
            <button onClick={(e) => { e.stopPropagation(); onRename(node); }} style={miniBtn}>改</button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(node); }} style={miniBtn}>删</button>
          </>
        )}
      </div>
      {node.is_dir && open && (node.children || []).map((child) => (
        <TreeNode
          key={child.path}
          node={child}
          activePath={activePath}
          selectedPath={selectedPath}
          expandedDirs={expandedDirs}
          onToggleDir={onToggleDir}
          onOpen={onOpen}
          onSelectDir={onSelectDir}
          onSelectPath={onSelectPath}
          onRename={onRename}
          onDelete={onDelete}
          level={level + 1}
        />
      ))}
    </div>
  );
};

const menuBtn = {
  background: "transparent",
  border: "none",
  color: "#d0d7de",
  textAlign: "left",
  fontSize: 12,
  cursor: "pointer",
  padding: "4px 6px",
};

const iconBtn = {
  background: "#161b22",
  border: "1px solid #30363d",
  color: "#9aa4b2",
  borderRadius: 4,
  fontSize: 11,
  cursor: "pointer",
  padding: "2px 5px",
};

const miniBtn = {
  background: "transparent",
  border: "none",
  color: "#6e7681",
  fontSize: 10,
  cursor: "pointer",
  padding: 0,
};

const railBtn = {
  writingMode: "vertical-rl",
  textOrientation: "mixed",
  background: "#161b22",
  border: "1px solid #30363d",
  color: "#9aa4b2",
  borderRadius: 6,
  fontSize: 12,
  cursor: "pointer",
  padding: "8px 5px",
  letterSpacing: 1,
};

const okBtn = {
  background: "#238636",
  border: "none",
  color: "#fff",
  borderRadius: 4,
  padding: "3px 10px",
  cursor: "pointer",
};

const dangerBtn = {
  background: "transparent",
  border: "1px solid #f85149",
  color: "#f85149",
  borderRadius: 4,
  padding: "3px 10px",
  cursor: "pointer",
};

export default CCFileTree;
